<template>
  <div
    class="sizes"
    :class="{ 'sizes--inside-quick-add': isInsideQuickAdd, 'sizes--error': hasError }"
    @mouseenter="preloadOverlayComponents"
  >
    <div v-if="isInsideQuickAdd" class="sizes__cover sizes__cover--bottom"></div>
    <div v-if="isInsideQuickAdd" class="sizes__cover sizes__cover--right"></div>
    <div
      v-for="(size, index) in variantSkus"
      :key="index"
      class="sizes__size"
      :class="{
        'sizes__size--1': variantSkusCount === 1,
        'sizes__size--2': variantIsSock,
        'sizes__size--5': variantSkusCount === 5,
        'sizes__size--no-stock': size.stock === 0,
        'sizes__size--selected': size.size === selectedSize,
      }"
      @click.prevent="
        handleSizeClick({ size: size.size, stock: size.stock, sku: size.sku }, $event)
      "
      v-data-test-id="'size'"
    >
      {{ size.size }}
    </div>
    <span
      v-if="variantSkusCount > 1"
      class="sizes__size sizes__size--info icon-info"
      :class="{
        'sizes__size--2': variantIsSock,
        'sizes__size--5': variantSkusCount === 5,
      }"
      @click="openSizeInfoModal"
      @mouseenter.once="loadFitIfNeeded"
      v-data-test-id="'size-guide'"
    ></span>
    <transition name="fade">
      <div class="sizes__error" v-if="hasError">
        {{ $t('pdp.purchase_pod.select_size') }}
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import buildClient from '@/api/client'
import productFitQuery from '@/api/product-fit.gql'
import getPageContext from '@/helpers/page-context'

export const UNSELECTED_SIZE = ''

export default {
  name: 'Sizes',
  props: {
    variant: {
      type: Object,
      required: true,
    },

    variantSkus: {
      type: Array,
      required: true,
    },

    resetSizeTimestamp: {
      type: Date,
    },

    variantIsSock: {
      type: Boolean,
      required: true,
    },

    isBackorderable: {
      type: Boolean,
      required: true,
    },

    imageUrl: {
      type: String,
      required: true,
    },

    productName: {
      type: String,
      required: true,
    },

    productType: {
      type: String,
      required: true,
    },

    hasError: {
      type: Boolean,
      default: false,
    },

    isInsideQuickAdd: {
      type: Boolean,
      default: false,
    },

    isSizeReset: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      selectedSize: '',
      productFit: {},
      currentSku: '',
    }
  },

  computed: {
    variantSkusCount() {
      return this.variantSkus.length
    },
  },

  watch: {
    resetSizeTimestamp() {
      const selectedStock = this.variantSkus.find(sku => sku.size === this.selectedSize)?.stock
      if (selectedStock === 0 || selectedStock === undefined) {
        this.selectedSize = ''
        this.$emit('select-size', '')
      }
    },

    isSizeReset() {
      if (this.isSizeReset) this.selectedSize = UNSELECTED_SIZE
    },
  },

  created() {
    if (this.variantSkusCount === 1 && this.variantSkus[0].stock > 0)
      this.handleSizeClick(this.variantSkus[0])
  },

  mounted() {
    if (On.isTouchDevice()) this.preloadOverlayComponents()
  },

  methods: {
    ...mapActions({
      showSizeGuideOverlay: 'overlay/showSizeGuideOverlay',
      showOutOfStockOverlay: 'overlay/showOutOfStockOverlay',
      preloadOutOfStockOverlay: 'overlay/preloadOutOfStockOverlay',
      preloadSizeGuideOverlay: 'overlay/preloadSizeGuideOverlay',
    }),

    loadFitIfNeeded() {
      if (!this.variant.fit && !this.productFit.sizeAndFit) this.fetchFitData()
    },

    async fetchFitData() {
      const { product } = await buildClient().query(productFitQuery, {
        sku: this.variant.spreeProduct.sku,
      })
      this.productFit = product.productVariantData.fit
    },

    handleSizeClick(selectedSku, $event) {
      if (selectedSku.stock <= 0) {
        this.showOutOfStockNotification(selectedSku.sku)
        this.trackOutOfStockActions(
          `${this.productName} || ${this.productType} || ${selectedSku.sku}`
        )
      } else {
        this.selectedSize = selectedSku.size
        this.$emit('select-size', this.selectedSize, $event)
      }
    },

    openSizeInfoModal($event) {
      this.showSizeGuideOverlay({
        fit: this.variant.fit || this.productFit,
        gender: Array.isArray(this.variant.gender) ? this.variant.gender[0] : this.variant.gender,
        imageUrl: this.imageUrl,
        isBackorderable: this.isBackorderable,
        productName: this.variant.productName || this.variant.spreeProduct.groupName,
        productType: this.variant.spreeProduct.productType,
        productUrl: this.variant.spreeProduct.productUrl || this.variant.productUrl,
        variantSkus: this.variantSkus,
      })
      On.Analytics.trackSizeInfoModal(getPageContext($event), 'info icon')
    },

    preloadOverlayComponents() {
      this.preloadOutOfStockOverlay()
      this.preloadSizeGuideOverlay()
    },

    showOutOfStockNotification(sku) {
      this.showOutOfStockOverlay({
        sku,
        imageUrl: this.imageUrl,
        isBackorderable: this.isBackorderable,
      })
    },

    trackOutOfStockActions(trackingInfo) {
      this.isBackorderable
        ? On.Analytics.trackOutOfStock.openBackorderModal(trackingInfo)
        : On.Analytics.trackOutOfStock.openNewsletterModal(trackingInfo)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Sizes';
</style>
